import React, { useState, useCallback } from 'react';
import { base44 } from '@/api/base44Client';
import {
  Loader2, Search, RefreshCw, AlertCircle, TrendingUp, TrendingDown, Minus,
  MousePointerClick, Eye, Percent, Gauge, Globe, FileText, HelpCircle, Lightbulb,
} from 'lucide-react';
import SeoActionPlan from '@/components/admin/marketing/SeoActionPlan';

// ISO country code -> display name (common ones; fallback to the code itself)
const COUNTRY_NAMES = {
  GB: 'United Kingdom', US: 'United States', DE: 'Germany', IT: 'Italy',
  FR: 'France', ES: 'Spain', NL: 'Netherlands', IE: 'Ireland', AU: 'Australia',
  CA: 'Canada', AE: 'United Arab Emirates', IN: 'India', SE: 'Sweden',
  PL: 'Poland', BE: 'Belgium', CH: 'Switzerland', DK: 'Denmark', NO: 'Norway',
  PT: 'Portugal', JP: 'Japan', NZ: 'New Zealand', ZA: 'South Africa',
  SA: 'Saudi Arabia', QA: 'Qatar', KW: 'Kuwait', BH: 'Bahrain', OM: 'Oman',
  AT: 'Austria', FI: 'Finland', CZ: 'Czechia', RO: 'Romania', GR: 'Greece',
  TR: 'Turkey', MX: 'Mexico', BR: 'Brazil', AR: 'Argentina', SG: 'Singapore',
  MY: 'Malaysia', TH: 'Thailand', KR: 'South Korea', CN: 'China', ID: 'Indonesia',
  PH: 'Philippines', VN: 'Vietnam',
};

const countryName = (code) => COUNTRY_NAMES[code] || code || 'Unknown';
const countryFlag = (code) =>
  code && code.length === 2
    ? `https://flagcdn.com/h40/${code.toLowerCase()}.png`
    : null;

const brandMatch = (q) => /\b(motion reality|motion realty|motion simulation|sim reality|mr sim)\b/i.test(q || '');

function pct(cur, prev) {
  if (!prev) return cur > 0 ? 100 : 0;
  return Math.round(((cur - prev) / prev) * 100);
}

function Delta({ cur, prev, suffix = '' }) {
  const d = cur - prev;
  if (prev === 0 && cur === 0) return <span className="text-white/30">—</span>;
  if (prev === 0) return <span className="text-green-400">+{cur}{suffix} (new)</span>;
  const p = pct(cur, prev);
  const Icon = p > 0 ? TrendingUp : p < 0 ? TrendingDown : Minus;
  const color = p > 0 ? 'text-green-400' : p < 0 ? 'text-red-400' : 'text-white/40';
  const sign = p > 0 ? '+' : '';
  return (
    <span className={`inline-flex items-center gap-1 ${color}`}>
      <Icon className="w-3.5 h-3.5" />
      {sign}{p}{suffix}
    </span>
  );
}

function SummaryCard({ icon: Icon, label, value, hint, delta }) {
  return (
    <div className="bg-[#0d0d0f] border border-white/5 p-4">
      <div className="flex items-center gap-2 text-white/30 mb-2">
        <Icon className="w-4 h-4" />
        <span className="text-[10px] font-bold tracking-widest">{label}</span>
      </div>
      <div className="text-white font-black text-2xl">{value}</div>
      <div className="mt-1.5 flex items-center justify-between">
        <span className="text-white/40 text-xs">{delta}</span>
      </div>
      <p className="text-white/30 text-[11px] mt-2 leading-snug">{hint}</p>
    </div>
  );
}

function positionBand(pos) {
  if (pos > 0 && pos <= 10) return 'page 1 (top results)';
  if (pos > 10 && pos <= 20) return 'page 2';
  if (pos > 20) return 'page 3+ (rarely seen)';
  return 'no ranking';
}

// Rule-based SEO explanation built from the fetched data
function buildInsights(data) {
  const t = data.totals;
  const qs = data.queries;
  const out = [];

  if (!t.impressions) {
    return [{
      tone: 'warn',
      title: 'No search presence yet',
      body: 'Google has recorded no impressions for your site in the last 28 days. This usually means the site is newly indexed, pages aren\'t being crawled, or the Search Console property covers the wrong domain. Submit your sitemap and use IndexNow to speed things up.',
    }];
  }

  // 1. Overall trend
  const impDelta = pct(t.impressions, t.prev_impressions);
  out.push({
    tone: impDelta >= 0 ? 'good' : 'warn',
    title: `Visibility ${impDelta >= 0 ? 'up' : 'down'} ${Math.abs(impDelta)}% vs previous 28 days`,
    body: `Your site appeared in Google ${t.impressions} times over the last 28 days (previous period: ${t.prev_impressions}). ${impDelta >= 0 ? 'More pages are surfacing in search, which is the first step to earning clicks.' : 'Your search visibility is slipping — check whether key pages were removed, changed URL, or lost rankings.'}`,
  });

  // 2. Brand vs generic
  const brand = qs.filter(q => brandMatch(q.key));
  const brandImp = brand.reduce((s, q) => s + q.impressions, 0);
  const genericImp = t.impressions - brandImp;
  const brandShare = t.impressions ? Math.round((brandImp / t.impressions) * 100) : 0;
  out.push({
    tone: brandShare > 60 ? 'warn' : 'info',
    title: `${brandShare}% of impressions come from your brand name`,
    body: brandShare > 60
      ? `Most people finding you already know "Motion Reality" / "Sim Reality". That means your existing audience is searching for you — but you're not yet being discovered by people searching generic terms like "sim racing store" or "motion simulator". Growing generic-term rankings is how you reach new customers.`
      : `A healthy mix of brand and generic searches are surfacing your site. Generic discovery is the engine of new-customer growth — keep building content around the terms you already appear for.`,
  });

  // 3. Position quality
  const page1 = qs.filter(q => q.position > 0 && q.position <= 10).length;
  const page2 = qs.filter(q => q.position > 10 && q.position <= 20).length;
  const page3 = qs.filter(q => q.position > 20).length;
  const avgPos = t.position;
  out.push({
    tone: avgPos > 20 ? 'warn' : 'good',
    title: `Average position ${avgPos.toFixed(0)} — ${positionBand(avgPos)}`,
    body: `Across all queries you rank around position ${avgPos.toFixed(0)} on average. ${page1} quer${page1 === 1 ? 'y' : 'ies'} sit on page 1, ${page2} on page 2, and ${page3} on page 3 or lower. ${page3 > page1 ? 'Most of your impressions are buried past page 2, where very few searchers look — improving on-page SEO and content depth for those terms is your biggest lever.' : 'You have solid page-1 presence for several terms; focus on protecting those and pushing page-2 terms onto page 1.'}`,
  });

  // 4. CTR
  const ctr = t.ctr;
  out.push({
    tone: ctr < 0.02 && t.impressions > 50 ? 'warn' : 'info',
    title: `Click-through rate ${(ctr * 100).toFixed(1)}%`,
    body: t.clicks === 0
      ? `You received 0 clicks from ${t.impressions} impressions. Either you're ranking too low for anyone to see your listings, or your page titles / meta descriptions aren't compelling enough to click. Improve both your rankings (SEO content) and your snippet text (the "PRODUCT SEO" / "PAGE SEO" tabs help here).`
      : `${t.clicks} people clicked out of ${t.impressions} who saw your listing — a CTR of ${(ctr * 100).toFixed(1)}%. ${ctr < 0.03 ? 'That\'s on the low side; more compelling titles and descriptions could lift it.' : 'That\'s a healthy rate — your snippets are attracting clicks.'}`,
  });

  // 5. Pages
  const topPages = data.pages.slice(0, 3);
  if (topPages.length) {
    out.push({
      tone: 'info',
      title: 'Top pages by impressions',
      body: `Your most-seen pages are ${topPages.map(p => shortenUrl(p.key)).join(', ')}. These are what Google feels confident showing — make sure their content is accurate, up to date, and targets the queries driving the impressions.`,
    });
  }

  return out;
}

function shortenUrl(u) {
  try {
    const url = new URL(u);
    return url.pathname === '/' ? 'homepage' : url.pathname;
  } catch {
    return u;
  }
}

function InsightRow({ insight }) {
  const toneClasses = {
    good: 'border-green-500/30 bg-green-500/5 text-green-300',
    warn: 'border-amber-500/30 bg-amber-500/5 text-amber-300',
    info: 'border-white/10 bg-white/[0.02] text-white/60',
  };
  const Icon = insight.tone === 'good' ? TrendingUp : insight.tone === 'warn' ? AlertCircle : Lightbulb;
  return (
    <div className={`flex items-start gap-3 border p-4 ${toneClasses[insight.tone]}`}>
      <Icon className="w-4 h-4 mt-0.5 shrink-0" />
      <div>
        <p className="font-bold text-white text-sm">{insight.title}</p>
        <p className="text-white/50 text-xs mt-1 leading-relaxed">{insight.body}</p>
      </div>
    </div>
  );
}

const TABS = [
  { key: 'queries', label: 'QUERIES', icon: Search },
  { key: 'pages', label: 'PAGES', icon: FileText },
  { key: 'countries', label: 'COUNTRIES', icon: Globe },
];

export default function SearchConsolePanel() {
  const [loading, setLoading] = useState(false);
  const [data, setData] = useState(null);
  const [error, setError] = useState('');
  const [tab, setTab] = useState('queries');

  const fetchData = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      const res = await base44.functions.invoke('gscSearchAnalytics', {});
      if (res?.error) throw new Error(res.error);
      setData(res.data);
    } catch (err) {
      setError(err.message || 'Failed to load search console data');
      setData(null);
    } finally {
      setLoading(false);
    }
  }, []);

  return (
    <div className="space-y-5">
      {/* Header */}
      <div className="border border-white/10 bg-[#111115]">
        <div className="flex items-center justify-between gap-3 px-5 py-4 border-b border-white/5">
          <div>
            <p className="text-white/30 text-[10px] font-bold tracking-widest">GOOGLE SEARCH CONSOLE</p>
            <h2 className="text-white font-bold text-base mt-0.5">Search Performance</h2>
            {data?.site_url && (
              <p className="text-white/30 text-xs mt-1 font-mono break-all">{data.site_url}</p>
            )}
          </div>
          <button
            onClick={fetchData}
            disabled={loading}
            className="flex items-center gap-2 border border-white/10 hover:border-primary/50 text-white/60 hover:text-white text-xs font-bold tracking-wider px-3 py-2 transition-all disabled:opacity-40"
          >
            {loading ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <RefreshCw className="w-3.5 h-3.5" />}
            {data ? 'REFRESH' : 'LOAD DATA'}
          </button>
        </div>

        {error && (
          <div className="flex items-start gap-2.5 px-5 py-3 bg-red-500/5 border-b border-red-500/20">
            <AlertCircle className="w-4 h-4 text-red-400 mt-0.5 shrink-0" />
            <p className="text-red-300/80 text-xs">{error}</p>
          </div>
        )}

        {!data && !loading && !error && (
          <div className="px-5 py-12 text-center">
            <Search className="w-10 h-10 text-white/10 mx-auto mb-3" />
            <p className="text-white/30 text-sm">Pull your Google search performance.</p>
            <p className="text-white/20 text-xs mt-1">Clicks, impressions, ranking position, top queries, pages and countries — with a plain-English explanation of how your SEO is doing.</p>
          </div>
        )}

        {loading && (
          <div className="flex items-center justify-center py-16">
            <Loader2 className="w-6 h-6 animate-spin text-primary" />
          </div>
        )}

        {data && !loading && (
          <div>
            {data.range && (
              <p className="px-5 py-2.5 text-[11px] text-white/30 border-b border-white/5">
                Last <span className="text-white/50 font-bold">{data.window_days}</span> days: <span className="font-mono text-white/50">{data.range}</span>
              </p>
            )}

            {/* Summary cards */}
            <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 px-5 py-4">
              <SummaryCard
                icon={MousePointerClick}
                label="CLICKS"
                value={data.totals.clicks}
                delta={<Delta cur={data.totals.clicks} prev={data.totals.prev_clicks} />}
                hint="Times people clicked your site in Google results."
              />
              <SummaryCard
                icon={Eye}
                label="IMPRESSIONS"
                value={data.totals.impressions}
                delta={<Delta cur={data.totals.impressions} prev={data.totals.prev_impressions} />}
                hint="Times your site appeared in search results (clicked or not)."
              />
              <SummaryCard
                icon={Percent}
                label="AVG CTR"
                value={`${(data.totals.ctr * 100).toFixed(1)}%`}
                delta={<Delta cur={Math.round(data.totals.ctr * 1000)} prev={Math.round(data.totals.prev_ctr * 1000)} suffix="‰" />}
                hint="Clicks ÷ impressions. Low CTR = your titles/snippets aren't enticing."
              />
              <SummaryCard
                icon={Gauge}
                label="AVG POSITION"
                value={data.totals.position ? data.totals.position.toFixed(1) : '—'}
                delta={data.totals.prev_position ? <span className={data.totals.position <= data.totals.prev_position ? 'text-green-400' : 'text-red-400'}>
                  {data.totals.position <= data.totals.prev_position ? '▲ improved' : '▼ slipped'}
                </span> : <span className="text-white/30">—</span>}
                hint="Average ranking (1 = top of page 1; 20+ = page 3, rarely seen)."
              />
            </div>

            {/* Tabs */}
            <div className="flex gap-1 px-5 border-b border-white/5">
              {TABS.map(t => (
                <button
                  key={t.key}
                  onClick={() => setTab(t.key)}
                  className={`flex items-center gap-2 px-3 py-2.5 text-[11px] font-bold tracking-widest border-b-2 transition-colors ${
                    tab === t.key
                      ? 'border-primary text-white'
                      : 'border-transparent text-white/40 hover:text-white/70'
                  }`}
                >
                  <t.icon className="w-3.5 h-3.5" /> {t.label}
                </button>
              ))}
            </div>

            {/* Tab content */}
            <div className="overflow-x-auto">
              {tab === 'queries' && (
                <QueryTable rows={data.queries} />
              )}
              {tab === 'pages' && (
                <PagesTable rows={data.pages} />
              )}
              {tab === 'countries' && (
                <CountriesTable rows={data.countries} />
              )}
            </div>
          </div>
        )}
      </div>

      {/* SEO explanation */}
      {data && !loading && (
        <div className="border border-white/10 bg-[#111115]">
          <div className="flex items-center gap-2 px-5 py-4 border-b border-white/5">
            <HelpCircle className="w-4 h-4 text-primary" />
            <div>
              <p className="text-white/30 text-[10px] font-bold tracking-widest">HOW IS YOUR SEO DOING?</p>
              <h2 className="text-white font-bold text-sm mt-0.5">Plain-English SEO health readout</h2>
            </div>
          </div>
          <div className="p-5 space-y-3">
            {buildInsights(data).map((insight, i) => (
              <InsightRow key={i} insight={insight} />
            ))}
            <div className="flex items-start gap-2 border border-primary/30 bg-primary/5 p-4">
              <Lightbulb className="w-4 h-4 text-primary mt-0.5 shrink-0" />
              <p className="text-white/60 text-xs leading-relaxed">
                <span className="text-primary font-bold">What to do next:</span> use the <span className="text-white font-bold">PRODUCT SEO</span> and <span className="text-white font-bold">PAGE SEO</span> tabs to generate optimised titles &amp; descriptions for the queries above, and the <span className="text-white font-bold">INDEXNOW</span> tab to tell Bing/Yandex to re-crawl once you publish changes.
              </p>
            </div>
          </div>
        </div>
      )}

      {/* One-click AI action plan built from the live data above */}
      {data && !loading && <SeoActionPlan data={data} />}
    </div>
  );
}

function QueryTable({ rows }) {
  if (!rows.length) return <EmptyState label="No search queries recorded for this period." />;
  return (
    <table className="w-full">
      <thead>
        <tr className="bg-[#0d0d0f] border-b border-white/5">
          <th className="text-left px-5 py-2.5 text-white/30 text-[10px] font-bold tracking-widest">QUERY</th>
          <th className="text-right px-3 py-2.5 text-white/30 text-[10px] font-bold tracking-widest">CLICKS</th>
          <th className="text-right px-3 py-2.5 text-white/30 text-[10px] font-bold tracking-widest">IMPRESSIONS</th>
          <th className="text-right px-3 py-2.5 text-white/30 text-[10px] font-bold tracking-widest hidden sm:table-cell">CTR</th>
          <th className="text-right px-5 py-2.5 text-white/30 text-[10px] font-bold tracking-widest">AVG POS</th>
        </tr>
      </thead>
      <tbody>
        {rows.map((row, i) => {
          const isBrand = brandMatch(row.key);
          return (
            <tr key={i} className="border-b border-white/5 hover:bg-white/[0.02] transition-colors">
              <td className="px-5 py-3 text-white/80 text-sm font-medium">
                {row.key || '(empty)'}
                {isBrand && <span className="ml-2 text-[9px] font-bold tracking-widest text-primary/70 border border-primary/30 px-1.5 py-0.5">BRAND</span>}
              </td>
              <td className="px-3 py-3 text-right text-white font-bold text-sm">{row.clicks}</td>
              <td className="px-3 py-3 text-right text-white/70 text-sm">{row.impressions}</td>
              <td className="px-3 py-3 text-right text-white/50 text-sm hidden sm:table-cell">{(row.ctr * 100).toFixed(1)}%</td>
              <td className="px-5 py-3 text-right text-sm">
                <span className={row.position > 0 && row.position <= 10 ? 'text-green-400 font-bold' : row.position > 20 ? 'text-white/40' : 'text-white/70'}>
                  {row.position ? row.position.toFixed(1) : '—'}
                </span>
              </td>
            </tr>
          );
        })}
      </tbody>
    </table>
  );
}

function PagesTable({ rows }) {
  if (!rows.length) return <EmptyState label="No page data recorded for this period." />;
  return (
    <table className="w-full">
      <thead>
        <tr className="bg-[#0d0d0f] border-b border-white/5">
          <th className="text-left px-5 py-2.5 text-white/30 text-[10px] font-bold tracking-widest">PAGE</th>
          <th className="text-right px-3 py-2.5 text-white/30 text-[10px] font-bold tracking-widest">CLICKS</th>
          <th className="text-right px-3 py-2.5 text-white/30 text-[10px] font-bold tracking-widest">IMPRESSIONS</th>
          <th className="text-right px-5 py-2.5 text-white/30 text-[10px] font-bold tracking-widest hidden sm:table-cell">AVG POS</th>
        </tr>
      </thead>
      <tbody>
        {rows.map((row, i) => (
          <tr key={i} className="border-b border-white/5 hover:bg-white/[0.02] transition-colors">
            <td className="px-5 py-3 text-white/80 text-xs font-mono break-all max-w-md">{shortenUrl(row.key)}</td>
            <td className="px-3 py-3 text-right text-white font-bold text-sm">{row.clicks}</td>
            <td className="px-3 py-3 text-right text-white/70 text-sm">{row.impressions}</td>
            <td className="px-5 py-3 text-right text-white/50 text-sm hidden sm:table-cell">{row.position ? row.position.toFixed(1) : '—'}</td>
          </tr>
        ))}
      </tbody>
    </table>
  );
}

function CountriesTable({ rows }) {
  if (!rows.length) return <EmptyState label="No country data recorded for this period." />;
  const maxImp = Math.max(...rows.map(r => r.impressions), 1);
  return (
    <table className="w-full">
      <thead>
        <tr className="bg-[#0d0d0f] border-b border-white/5">
          <th className="text-left px-5 py-2.5 text-white/30 text-[10px] font-bold tracking-widest">COUNTRY</th>
          <th className="text-right px-3 py-2.5 text-white/30 text-[10px] font-bold tracking-widest">CLICKS</th>
          <th className="text-left px-5 py-2.5 text-white/30 text-[10px] font-bold tracking-widest w-[40%]">IMPRESSIONS</th>
        </tr>
      </thead>
      <tbody>
        {rows.map((row, i) => {
          const flag = countryFlag(row.key);
          const pctShare = Math.round((row.impressions / maxImp) * 100);
          return (
            <tr key={i} className="border-b border-white/5 hover:bg-white/[0.02] transition-colors">
              <td className="px-5 py-3">
                <div className="flex items-center gap-2.5">
                  {flag ? (
                    <img src={flag} alt={row.key} className="w-6 h-4 object-cover" loading="lazy" />
                  ) : (
                    <Globe className="w-4 h-4 text-white/30" />
                  )}
                  <span className="text-white/80 text-sm">{countryName(row.key)}</span>
                  <span className="text-white/25 text-[10px] font-mono">{row.key}</span>
                </div>
              </td>
              <td className="px-3 py-3 text-right text-white font-bold text-sm">{row.clicks}</td>
              <td className="px-5 py-3">
                <div className="flex items-center gap-3">
                  <div className="flex-1 h-1.5 bg-white/5">
                    <div className="h-full bg-primary" style={{ width: `${pctShare}%` }} />
                  </div>
                  <span className="text-white/60 text-xs font-mono w-10 text-right">{row.impressions}</span>
                </div>
              </td>
            </tr>
          );
        })}
      </tbody>
    </table>
  );
}

function EmptyState({ label }) {
  return (
    <div className="px-5 py-12 text-center">
      <p className="text-white/30 text-sm">{label}</p>
    </div>
  );
}